import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { agendaLabService, telegramIntegrationService } from '../../services/api'

const unwrapPayload = (response) => response?.data?.data || response?.data || null

export default function AgendaLabTelegram() {
  const { user } = useAuth()
  const [chatId, setChatId] = useState('')
  const [status, setStatus] = useState(null)
  const [publicSettings, setPublicSettings] = useState({
    isEnabled: true,
    publicAgendaUrl: '',
    displayName: 'AgendaLab',
    description: '',
    nequiKey: '',
    nequiKeyType: 'telefono'
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSavingPublicSettings, setIsSavingPublicSettings] = useState(false)

  const publicAgendaUrl = useMemo(() => {
    const relativeUrl = publicSettings?.publicAgendaUrl || ''
    if (!relativeUrl) return ''
    if (relativeUrl.startsWith('http')) return relativeUrl
    return `${window.location.origin}${relativeUrl}`
  }, [publicSettings])

  const phoneNumber = user?.phone || user?.PhoneNumber || user?.Phone || ''

  useEffect(() => {
    const load = async () => {
      try {
        setError('')
        setSuccess('')
        setIsLoading(true)

        const [telegramResponse, publicSettingsResponse] = await Promise.allSettled([
          telegramIntegrationService.getStatus(),
          agendaLabService.getPublicBookingSettings()
        ])

        const telegramPayload = telegramResponse.status === 'fulfilled' ? unwrapPayload(telegramResponse.value) : null
        const publicPayload = publicSettingsResponse.status === 'fulfilled' ? unwrapPayload(publicSettingsResponse.value) : null

        setStatus(telegramPayload)
        setPublicSettings((current) => ({
          ...current,
          isEnabled: publicPayload?.isEnabled ?? publicPayload?.IsEnabled ?? current.isEnabled,
          publicAgendaUrl: publicPayload?.publicAgendaUrl || publicPayload?.PublicAgendaUrl || current.publicAgendaUrl,
          displayName: publicPayload?.displayName || publicPayload?.DisplayName || current.displayName,
          description: publicPayload?.description || publicPayload?.Description || current.description,
          nequiKey: publicPayload?.nequiKey || publicPayload?.NequiKey || current.nequiKey,
          nequiKeyType: publicPayload?.nequiKeyType || publicPayload?.NequiKeyType || current.nequiKeyType
        }))

        if (telegramPayload?.chatId) {
          setChatId(telegramPayload.chatId)
        }

        if (
          telegramResponse.status === 'rejected' &&
          publicSettingsResponse.status === 'rejected'
        ) {
          throw telegramResponse.reason || publicSettingsResponse.reason || new Error('No se pudo cargar la configuracion actual de AgendaLab.')
        }
      } catch (err) {
        setError(err?.message || 'No se pudo cargar la configuracion actual de AgendaLab.')
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [])

  const connect = async (e) => {
    e.preventDefault()
    try {
      setError('')
      setSuccess('')
      const response = await telegramIntegrationService.connect(chatId, true)
      const payload = unwrapPayload(response)
      setStatus(payload)
      if (payload?.chatId) {
        setChatId(payload.chatId)
      }
      setSuccess('La configuracion de Telegram quedo guardada correctamente.')
    } catch (err) {
      setError(err?.message || 'No se pudo guardar el chatId de Telegram.')
    }
  }

  const savePublicSettings = async (e) => {
    e.preventDefault()
    try {
      setIsSavingPublicSettings(true)
      setError('')
      setSuccess('')

      const response = await agendaLabService.savePublicBookingSettings({
        isEnabled: publicSettings.isEnabled,
        displayName: publicSettings.displayName,
        description: publicSettings.description,
        nequiKey: publicSettings.nequiKey,
        nequiKeyType: publicSettings.nequiKeyType
      })

      const payload = unwrapPayload(response)
      setPublicSettings((current) => ({
        ...current,
        isEnabled: payload?.isEnabled ?? payload?.IsEnabled ?? current.isEnabled,
        publicAgendaUrl: payload?.publicAgendaUrl || payload?.PublicAgendaUrl || current.publicAgendaUrl,
        displayName: payload?.displayName || payload?.DisplayName || current.displayName,
        description: payload?.description || payload?.Description || current.description,
        nequiKey: payload?.nequiKey || payload?.NequiKey || current.nequiKey,
        nequiKeyType: payload?.nequiKeyType || payload?.NequiKeyType || current.nequiKeyType
      }))
      setSuccess('La configuracion publica quedo guardada correctamente.')
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la configuracion publica.')
    } finally {
      setIsSavingPublicSettings(false)
    }
  }

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value || '')
      setSuccess(`${label} copiado correctamente.`)
      setError('')
    } catch {
      setError(`No se pudo copiar ${label.toLowerCase()}.`)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Configuracion</h1>
        <p className="text-sm text-gray-500 mt-1">
          Revisa tu configuracion actual de Telegram, tu numero de telefono, tu llave de Nequi y el link publico para compartir con tus clientes.
        </p>
      </div>

      {isLoading && <div className="card p-3 text-sm text-gray-600">Cargando configuracion actual...</div>}
      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>}
      {success && <div className="card p-3 text-sm text-green-700 bg-green-50 border border-green-200">{success}</div>}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card p-5 space-y-3 text-sm text-gray-700">
          <h2 className="text-lg font-semibold text-gray-900">Telegram</h2>
          <div>Estado actual: <strong>{status?.connected ? 'Conectado' : 'Desconectado'}</strong></div>
          <div>ChatId configurado: <strong>{status?.chatId || 'Sin configurar'}</strong></div>
          <div>Persistencia: <strong>{status?.chatId ? 'Guardado en base de datos' : 'Pendiente'}</strong></div>
          {status?.connected && status?.chatId && (
            <div className="inline-flex rounded-full border border-green-300 bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
              Telegram conectado y configurado
            </div>
          )}
        </div>

        <div className="card p-5 space-y-3 text-sm text-gray-700">
          <h2 className="text-lg font-semibold text-gray-900">Datos para compartir</h2>
          <div>Mi numero de telefono: <strong>{phoneNumber || 'Sin configurar'}</strong></div>
          <div>Mi llave de Nequi: <strong>{publicSettings?.nequiKey || 'Sin configurar'}</strong></div>
          <div>Tipo de llave: <strong>{publicSettings?.nequiKeyType || 'Sin configurar'}</strong></div>
          <div>Nombre publico: <strong>{publicSettings?.displayName || 'AgendaLab'}</strong></div>
        </div>
      </div>

      <div className="card p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Mi link para agendar</h2>
          <p className="text-sm text-gray-500 mt-1">
            Este link esta listo para copiar y pegar. Compartelo con tus clientes para que puedan reservar contigo.
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 break-all">
          {publicAgendaUrl || 'Aun no hay link publico disponible. Guarda primero la configuracion en Programacion.'}
        </div>

        <div className="flex flex-wrap gap-3">
          <button className="btn btn-secondary" type="button" onClick={() => copyValue(publicAgendaUrl, 'Link publico')} disabled={!publicAgendaUrl}>
            Copiar link
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => copyValue(phoneNumber, 'Numero de telefono')} disabled={!phoneNumber}>
            Copiar telefono
          </button>
          <button className="btn btn-secondary" type="button" onClick={() => copyValue(publicSettings?.nequiKey || '', 'Llave de Nequi')} disabled={!publicSettings?.nequiKey}>
            Copiar Nequi
          </button>
        </div>
      </div>

      <form className="card p-5 space-y-4" onSubmit={savePublicSettings}>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Configuracion publica y Nequi</h2>
          <p className="text-sm text-gray-500 mt-1">
            Aqui administras los datos visibles de tu agenda y la llave de pago para reservas.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre visible</label>
            <input
              className="input"
              value={publicSettings.displayName}
              onChange={(e) => setPublicSettings((current) => ({ ...current, displayName: e.target.value }))}
              placeholder="Nombre de la agenda publica"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de llave Nequi</label>
            <select
              className="input"
              value={publicSettings.nequiKeyType}
              onChange={(e) => setPublicSettings((current) => ({ ...current, nequiKeyType: e.target.value }))}
            >
              <option value="telefono">Telefono</option>
              <option value="correo">Correo</option>
              <option value="documento">Documento</option>
              <option value="alfanumerica">Alfanumerica</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Llave Nequi</label>
            <input
              className="input"
              value={publicSettings.nequiKey}
              onChange={(e) => setPublicSettings((current) => ({ ...current, nequiKey: e.target.value }))}
              placeholder="Numero o llave para pago manual"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripcion publica</label>
            <textarea
              className="input"
              rows={4}
              value={publicSettings.description}
              onChange={(e) => setPublicSettings((current) => ({ ...current, description: e.target.value }))}
              placeholder="Mensaje corto que vera el usuario al abrir tu agenda publica."
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={publicSettings.isEnabled}
                onChange={(e) => setPublicSettings((current) => ({ ...current, isEnabled: e.target.checked }))}
              />
              <span>
                <span className="block font-medium text-gray-900">Habilitar agenda publica</span>
                <span className="mt-1 block text-xs text-gray-500">Activa o pausa las reservas visibles para el usuario final.</span>
              </span>
            </label>
          </div>
        </div>

        <button className="btn btn-primary" type="submit" disabled={isSavingPublicSettings}>
          {isSavingPublicSettings ? 'Guardando...' : 'Guardar configuracion publica'}
        </button>
      </form>

      <form className="card p-5 space-y-4" onSubmit={connect}>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Actualizar Telegram</h2>
          <p className="text-sm text-gray-500 mt-1">
            Si necesitas cambiar tu ChatId, hazlo aqui.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ChatId de Telegram</label>
          <input className="input" placeholder="ChatId" value={chatId} onChange={(e) => setChatId(e.target.value)} required />
        </div>

        <button className="btn btn-primary" type="submit">
          {status?.chatId ? 'Actualizar chatId' : 'Guardar Telegram'}
        </button>
      </form>
    </div>
  )
}
